/**
 * @voltxampere/ui — KaTeX renderer wrapper (Sprint 14, ADR-0020)
 *
 * Thin shim over `katex.render`. Three responsibilities:
 *
 *   1. Apply Sprint 14 standard render options (no `throwOnError`,
 *      output html, `trust: false` so user-edited templates can
 *      never enable `\href` / `\url` macros).
 *   2. Tag the rendered DOM with Zenit voice classes
 *      (`zenit-var`, `zenit-num`) so the per-app CSS overrides in
 *      `apps/web/src/styles/katex-overrides.css` can colour
 *      variables italic/brass and numbers monospace/ivory.
 *   3. On failure, leave a readable placeholder in the container
 *      and report through `onError` callback so the inspector can
 *      surface the issue (Kural 9 — fake fallback yok, ama
 *      sayfayı çökertmeye değecek bir şey de değil).
 *
 * The KaTeX import is `import katex from "katex"`; the package
 * ships an ESM build that resolves cleanly under Vite + Vitest.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import katex from "katex";
/**
 * Render a single formula into `container`. The container is
 * cleared first so successive `selectedNodes` updates do not
 * accumulate stale DOM.
 */
export function renderFormula(template, container, opts = {}) {
    container.innerHTML = "";
    try {
        katex.render(template.symbolic, container, {
            throwOnError: false,
            errorColor: "var(--danger, #c53030)",
            output: "html",
            trust: false,
            strict: "ignore",
        });
        applyZenitVoice(container, template.vars);
        return true;
    }
    catch (err) {
        container.textContent = template.symbolic;
        opts.onError?.(err);
        return false;
    }
}
/**
 * Walk the rendered KaTeX DOM and tag every variable token in
 * `vars` with the `zenit-var` class. Numeric literals (KaTeX
 * already emits `<span class="mord">` for digits) get the
 * `zenit-num` class. Pure DOM mutation; no return.
 *
 * The matching is by the variable's *first character* — KaTeX
 * splits multi-character identifiers into separate `<span>`s
 * (`I_C` → "I" then a subscript "C"), so we tag the leading
 * letter and let CSS inherit the styling onto the subscript via
 * the descendant selector.
 */
export function applyZenitVoice(container, vars) {
    const varHeads = new Set(vars.map((v) => stripLatex(v).charAt(0)).filter(Boolean));
    const ords = container.querySelectorAll(".katex .mord");
    ords.forEach((node) => {
        const text = node.textContent;
        if (typeof text !== "string" || text.length === 0)
            return;
        if (varHeads.has(text.charAt(0)) && /^[A-Za-zα-ωΑ-Ω]/.test(text)) {
            node.classList.add("zenit-var");
        }
        else if (/^[0-9.]/.test(text)) {
            node.classList.add("zenit-num");
        }
    });
}
/**
 * Sprint 56b — Render a raw LaTeX string (no `FormulaTemplate` wrapper).
 * Used by the holographic formula overlay which produces dynamic
 * substituted expressions ("V_{out} = 5\,V") that don't fit the
 * static FORMULA_REGISTRY entry shape.
 *
 * Same KaTeX options as {@link renderFormula} so the visual matches
 * the inspector formula surface byte-for-byte (no inert macros, no
 * `throw` on parse failure).
 */
export function renderRawLatex(latex, container, opts = {}) {
    container.innerHTML = "";
    try {
        katex.render(latex, container, {
            throwOnError: false,
            errorColor: "var(--danger, #c53030)",
            output: "html",
            trust: false,
            strict: "ignore",
        });
        return true;
    }
    catch (err) {
        container.textContent = latex;
        opts.onError?.(err);
        return false;
    }
}
/** Strip a LaTeX backslash macro down to its display character. */
function stripLatex(s) {
    if (s.startsWith("\\")) {
        // `\beta` → "β", `\phi` → "φ" (KaTeX renders these as Unicode
        // glyphs we can match against). Keep the inner name lower-
        // cased so `\Phi` still matches the rendered "Φ".
        return s.slice(1);
    }
    return s;
}
//# sourceMappingURL=katex-renderer.js.map